import React, { useState, useEffect, useCallback } from 'react';
import ReportIssueModal from './ReportIssueModal';

/**
 * Global listener for `akf:report-issue` events. Captures a screenshot of
 * the page BEFORE the modal opens (so the modal isn't in the shot), then
 * renders the AI-prefilled report modal. Mounted OUTSIDE AppErrorBoundary
 * so crash screens can still open it.
 */
export default function ReportIssueHost() {
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [fingerprint, setFingerprint] = useState(null);
  const [screenshot, setScreenshot] = useState(null);

  const onEvent = useCallback(async (e) => {
    const { issueContext, fingerprint: fp } = e.detail || {};
    let shot = null;
    try {
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(document.body, {
        useCORS: true,
        logging: false,
        scale: Math.min(window.devicePixelRatio || 1, 1.5),
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        x: window.scrollX,
        y: window.scrollY,
        width: window.innerWidth,
        height: window.innerHeight,
      });
      shot = canvas.toDataURL('image/jpeg', 0.7);
    } catch (_) {
      shot = null; // screenshot is best-effort, never block the report
    }
    setCtx(issueContext || {});
    setFingerprint(fp || null);
    setScreenshot(shot);
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener('akf:report-issue', onEvent);
    return () => window.removeEventListener('akf:report-issue', onEvent);
  }, [onEvent]);

  if (!open) return null;
  return (
    <ReportIssueModal
      open={open}
      onClose={() => { setOpen(false); setCtx(null); setScreenshot(null); }}
      issueContext={ctx}
      fingerprint={fingerprint}
      screenshotDataUrl={screenshot}
    />
  );
}
